import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export default function TextInput({ initialStocks, onStocksChange, onSubmit }) {
  const [text, setText] = useState('');
  const [parsedStocks, setParsedStocks] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialStocks && initialStocks.length > 0) {
      const formattedText = initialStocks
        .map(stock => `${stock.ticker},${stock.shares};`)
        .join('\n');
      setText(formattedText);
    }
  }, [initialStocks]);

  const parseStocks = (inputText) => {
    setError('');
    const entries = inputText.split(/[;\n]/).filter(entry => entry.trim() !== '');
    const stocks = [];
    
    for (const entry of entries) {
      const match = entry.match(/^([A-Za-z]+)\s*,\s*(\d+)$/);
      
      if (!match) {
        setError(`Invalid format in entry: "${entry}". Please use format "TICKER,SHARES;"`);
        return null;
      }
      
      const [, ticker, sharesStr] = match;
      const shares = parseInt(sharesStr, 10);
      
      if (isNaN(shares)) {
        setError(`Invalid number of shares in entry: "${entry}"`);
        return null;
      }
      
      stocks.push({
        ticker: ticker.toUpperCase(),
        shares: shares
      });
    }
    
    return stocks;
  };

  const handleTextChange = (event) => {
    const newText = event.target.value;
    setText(newText);
    
    if (newText.trim()) {
      const parsed = parseStocks(newText);
      if (parsed) {
        setParsedStocks(parsed);
        if (typeof onStocksChange === 'function') {
          onStocksChange(parsed);
        }
      }
    } else {
      setParsedStocks([]);
      if (typeof onStocksChange === 'function') {
        onStocksChange([]);
      }
    }
  };

  const handleRemoveStock = (indexToRemove) => {
    const updatedStocks = parsedStocks.filter((_, index) => index !== indexToRemove);
    setParsedStocks(updatedStocks);
    const updatedText = updatedStocks
      .map(stock => `${stock.ticker},${stock.shares};`)
      .join('\n');
    setText(updatedText);
    if (typeof onStocksChange === 'function') {
      onStocksChange(updatedStocks);
    }
  };

  const handleSubmit = async () => {
    if (parsedStocks.length === 0) {
      setError('Please add at least one stock');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const portfolio = parsedStocks.reduce((acc, stock) => {
        acc[stock.ticker] = stock.shares;
        return acc;
      }, {});
      
      if (typeof onSubmit === 'function') {
        await onSubmit(portfolio);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enter your portfolio in the format "TICKER,SHARES;" (one per line or separated by semicolons)
        <br />
        Example:
        <br />
        AAPL,100;
        <br />
        MSFT,50;
      </Typography>
      
      <TextField
        multiline
        rows={10}
        value={text}
        onChange={handleTextChange}
        fullWidth
        variant="outlined"
        placeholder="AAPL,100;&#10;MSFT,50;"
        sx={{ mb: 2 }}
      />
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {parsedStocks.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticker</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedStocks.map((stock, index) => (
                <TableRow key={index}>
                  <TableCell>{stock.ticker}</TableCell>
                  <TableCell align="right">{stock.shares}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveStock(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={isSubmitting || parsedStocks.length === 0}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Analyze Portfolio'
        )}
      </Button>
    </Box>
  );
}

TextInput.defaultProps = {
  onStocksChange: () => {},
  onSubmit: () => {},
};